import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { OnboardingResource, OnboardingResourceCategory } from "@shared/schema";
import { Pencil, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CATEGORY_LABELS } from "@/lib/category-labels";
import { labelForRenderType } from "./render-type-labels";

// Single resource row inside a milestone column on PG-TPL-CANVAS.
// Includes drag handle (UC-TPL-014), remove button (UC-TPL-016), and
// click-on-title preview (UC-TPL-022).

type Props = {
  assignmentId: string;
  resource: OnboardingResource;
  isOwner: boolean;
  busy?: boolean;
  onPreview: (resource: OnboardingResource) => void;
  onEdit: (resource: OnboardingResource) => void;
  onRemove: (assignmentId: string) => void;
};

export function AssignmentRow({
  assignmentId,
  resource,
  isOwner,
  busy,
  onPreview,
  onEdit,
  onRemove,
}: Props) {
  const sortable = useSortable({ id: assignmentId, disabled: !isOwner });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0 : 1,
  };

  return (
    <li
      ref={sortable.setNodeRef}
      style={style}
      className="group flex items-center gap-2 px-4 py-3 hover:bg-accent/30"
      data-testid={`assignment-row-${assignmentId}`}
    >
      {isOwner ? (
        <button
          type="button"
          className="text-muted-foreground/60 hover:text-muted-foreground w-6 text-center cursor-grab leading-none"
          aria-label="Mover"
          data-testid={`assignment-handle-${assignmentId}`}
          {...sortable.attributes}
          {...sortable.listeners}
        >
          ⋮⋮
        </button>
      ) : (
        <span className="w-6" aria-hidden="true" />
      )}

      {resource.renderType === "todo" ? (
        <div className="flex-1 text-left" data-testid={`assignment-title-${assignmentId}`}>
          <p className="text-sm font-medium">{resource.title}</p>
          <p className="text-xs text-muted-foreground">{labelForRenderType(resource.renderType)}</p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onPreview(resource)}
          className="flex-1 text-left"
          data-testid={`assignment-title-${assignmentId}`}
        >
          <p className="text-sm font-medium">{resource.title}</p>
          {resource.renderType && (
            <p className="text-xs text-muted-foreground">
              {labelForRenderType(resource.renderType)}
            </p>
          )}
        </button>
      )}

      {resource.category && (
        <Badge
          variant="outline"
          className="ml-auto shrink-0"
          data-testid={`assignment-category-${assignmentId}`}
        >
          {CATEGORY_LABELS[resource.category as OnboardingResourceCategory]}
        </Badge>
      )}

      {isOwner && (
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onEdit(resource)}
            disabled={busy}
            aria-label="Editar"
            title="Editar"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            data-testid={`assignment-edit-${assignmentId}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onRemove(assignmentId)}
            disabled={busy}
            aria-label="Remover"
            title="Remover"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            data-testid={`assignment-remove-${assignmentId}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </li>
  );
}
